import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {StorableInput} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TransferStructuredTableRowMetadata} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  bulkFetchParentStorableCapacityModel,
  isUnfinalizedParentStorableSample,
  ParentStorableCapacityInfo,
  readParentStorableCapacityFromCache,
} from 'notebook/structured-tables/row-updaters/inventoryParentStorableRowUpdaterUtils';
import {
  PlatePositionLookupInfo,
  PrimarySampleLookupType,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromBioentitySummary,
  getTypedValueFromPartySummaries,
  getTypedValueFromSampleRestrictionStatus,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {BulkContainerInfoQuery, BulkContainerInfoQueryVariables} from './__generated/BulkContainerInfoQuery';
import {BulkEntityInfoQuery, BulkEntityInfoQueryVariables} from './__generated/BulkEntityInfoQuery';
import {
  BulkParentStorableQuery,
  BulkParentStorableQueryVariables,
} from './__generated/BulkParentStorableQuery';
import {BulkPlateSizeQuery, BulkPlateSizeQueryVariables} from './__generated/BulkPlateSizeQuery';
import {BulkPlateWellInfoQuery, BulkPlateWellInfoQueryVariables} from './__generated/BulkPlateWellInfoQuery';
import {buildStandardUpdatedCells} from './updateRowUtils';

type PlateCellInfo = ExtractByTypename<BulkPlateWellInfoQuery['directStorables'][number], 'PlateNode'>;

type PlateInfo = ExtractByTypename<BulkPlateSizeQuery['plates'][number], 'PlateNode'>;

type PlateSizeQuery_storable_PlateNode_storableSchema = PlateInfo['storableSchema'];

type PlateSchemaInfo = ExtractByTypename<PlateSizeQuery_storable_PlateNode_storableSchema, 'PlateSchemaNode'>;

type ContainerInfo = ExtractByTypename<BulkContainerInfoQuery['containers'][number], 'ContainerNode'>;

type ParentStorableInfo = ExcludeInaccessible<BulkParentStorableQuery['storables'][number]>;

type EntityInfo = ExtractByTypename<BulkEntityInfoQuery['bioentities'][number], 'BasicFolderItemNode'>;

export const BULK_PARENT_STORABLE_QUERY = gql`
  query BulkParentStorableQuery($parentStorableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $parentStorableIds) {
      ...ParentStorableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_PLATE_WELL_INFO_QUERY = gql`
  query BulkPlateWellInfoQuery($inputs: [StorableInput!]!) @skipAudit @engComponent(name: INVENTORY) {
    directStorables(inputs: $inputs) {
      ... on StorableInterface {
        id
      }
      ...PlateWellInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateWellInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_PLATE_SIZE_QUERY = gql`
  query BulkPlateSizeQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ...PlateSizeGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('PlateSize').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_CONTAINER_INFO_QUERY = gql`
  query BulkContainerInfoQuery($containerIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ...ContainerInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ContainerInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BULK_ENTITY_INFO_QUERY = gql`
  query BulkEntityInfoQuery($entityIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    bioentities(ids: $entityIds) {
      ...RegistryItemInfoGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('RegistryItemInfo').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const isPlateCellInfoOrInaccessible = (
  s: BulkPlateWellInfoQuery['directStorables'][number]
): s is PlateCellInfo | InaccessibleInfoGraphQL =>
  ['PlateNode', 'InacessibleInfoNode'].includes(s.__typename);

type TransferTableModelInfo = PlateInfo | PlateCellInfo | ContainerInfo | EntityInfo | ParentStorableInfo;

function isPlateCellInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is PlateCellInfo {
  return transferTableModelInfo.__typename === 'PlateNode';
}

function isContainerInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is ContainerInfo {
  return transferTableModelInfo.__typename === 'ContainerNode';
}

function isEntityInfo(transferTableModelInfo: TransferTableModelInfo): transferTableModelInfo is EntityInfo {
  const typenames = ['BasicFolderItemNode', 'SequenceNode', 'ProteinNode', 'MixtureNode', 'MoleculeNode'];
  return typenames.includes(transferTableModelInfo.__typename);
}

function isParentStorableInfo(
  transferTableModelInfo: TransferTableModelInfo
): transferTableModelInfo is ParentStorableInfo {
  const typenames = ['LocationNode', 'BoxNode', 'PlateNode'];
  return typenames.includes(transferTableModelInfo.__typename);
}

function isParentStorableCapacityInfo(
  modelInfo: TransferTableModelInfo | ParentStorableCapacityInfo
): modelInfo is ParentStorableCapacityInfo {
  return modelInfo.hasOwnProperty('occupiedCapacity');
}

function isPlateSchemaInfo(
  plateSchema: PlateSizeQuery_storable_PlateNode_storableSchema
): plateSchema is PlateSchemaInfo {
  return plateSchema.__typename === 'PlateSchemaNode';
}

function getPlateWellCoordinates(
  plateSchema: PlateSchemaInfo,
  plateWellPosition: Position | string
): Position {
  let rowIndex;
  let columnIndex;
  if (typeof plateWellPosition === 'string') {
    try {
      const plateHeight = plateSchema.height;
      const plateWidth = plateSchema.width;
      ({rowIndex, columnIndex} = parseGridPosition({
        position: plateWellPosition,
        gridHeight: plateHeight,
        gridWidth: plateWidth,
      }));
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        throw new Error('Position should be valid. Sample has already been transferred to this position');
      } else {
        throw e;
      }
    }
  } else {
    rowIndex = plateWellPosition.rowIndex;
    columnIndex = plateWellPosition.columnIndex;
  }
  return {rowIndex, columnIndex};
}

function updateBioentityCell(model: TransferTableModelInfo): LiveCellValue {
  if (isEntityInfo(model)) {
    return getTypedValueFromBioentitySummary(model);
  }
  return 'NO_VALUE';
}

function updateContainersCell(model: TransferTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return getTypedValueFromStorableSummaries([model]);
  }
  return 'NO_VALUE';
}

function updatePlateCell(model: TransferTableModelInfo): LiveCellValue {
  if (isPlateCellInfo(model)) {
    // If the model contains plate well details, the model must be a well plate.
    return getTypedValueFromStorableSummaries([model]);
  } else if (isParentStorableInfo(model)) {
    // Matrix plate parent storable.
    const plate = model;
    if (plate?.__typename !== 'PlateNode' || isInaccessible(plate)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([plate]);
  }
  return 'NO_VALUE';
}

function updateBoxCell(model: TransferTableModelInfo): LiveCellValue {
  if (isParentStorableInfo(model)) {
    const box = model;
    // Box parent storable.
    if (box?.__typename !== 'BoxNode' || isInaccessible(box)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([box]);
  }
  return 'NO_VALUE';
}

function updateLocationCell(model: TransferTableModelInfo): LiveCellValue {
  if (isParentStorableInfo(model)) {
    const location = model;
    // Location parent storable.
    if (location?.__typename !== 'LocationNode' || isInaccessible(location)) {
      return 'NO_VALUE';
    }
    return getTypedValueFromStorableSummaries([location]);
  }
  return 'NO_VALUE';
}

function updateTagValueCell(model: TransferTableModelInfo, column: StructuredTableColumn): LiveCellValue {
  if (isContainerInfo(model)) {
    const matchingTag = model.tagsJson.find((tag: SchemaTag) =>
      isColumnForSchemaField(column, tag.tagSchemaField)
    );
    if (matchingTag) {
      return convertTagToTypedValue(matchingTag);
    }
  } else if (isPlateCellInfo(model)) {
    if (model.cell != null && !isInaccessible(model.cell.container)) {
      const matchingTag = model.cell.container.tagsJson.find((tag: SchemaTag) =>
        isColumnForSchemaField(column, tag.tagSchemaField)
      );
      if (matchingTag) {
        return convertTagToTypedValue(matchingTag);
      }
    }
  }
  return 'NO_VALUE';
}

function updateSampleControlRolesCell(
  model: TransferTableModelInfo,
  modelColumnGroup: ColumnGroup | null,
  columnGroup: ColumnGroup | null
): LiveCellValue {
  const sampleRolesKey =
    columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
      ? 'sampleOwners'
      : columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
        ? 'restrictedSampleUsers'
        : null;
  if (modelColumnGroup !== TransferTableColumnGroup.TARGET || sampleRolesKey == null) {
    return 'NO_VALUE';
  }
  if (isContainerInfo(model)) {
    const sampleRoles = model.sampleRoles[sampleRolesKey];
    return getTypedValueFromPartySummaries(sampleRoles);
  }
  return 'NO_VALUE';
}

function updateSampleRestrictionStatusCell(model: TransferTableModelInfo): LiveCellValue {
  if (isContainerInfo(model)) {
    return getTypedValueFromSampleRestrictionStatus(model.restrictionStatus);
  }
  return 'NO_VALUE';
}

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  // Build promise for bulk fetching containers
  const containerIds = sampleLookupInfos
    .filter((info) => APIUtil.isContainer(info.idForLookup))
    .map((info) => info.idForLookup);
  const containerPromise = doQuerySafe<BulkContainerInfoQuery, BulkContainerInfoQueryVariables>(
    {query: BULK_CONTAINER_INFO_QUERY},
    {variables: {containerIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.containers);

  // Build promise for bulk fetching bioentities
  const entityIds = sampleLookupInfos
    .filter((info) => APIUtil.isBiologicalEntity(info.idForLookup))
    .map((info) => info.idForLookup);
  const entityPromise = doQuerySafe<BulkEntityInfoQuery, BulkEntityInfoQueryVariables>(
    {query: BULK_ENTITY_INFO_QUERY},
    {variables: {entityIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.bioentities);

  // Build promise for bulk fetching unfinalized parents
  const infoFilter = (info: SampleLookupInfo): boolean =>
    (info.sampleType === PrimarySampleLookupType.DIRECT && APIUtil.isPlate(info.idForLookup)) || //  isSampleMatrixPlate
    APIUtil.isBox(info.idForLookup) ||
    APIUtil.isLocation(info.idForLookup);
  const filteredInfos = sampleLookupInfos.filter((info) => infoFilter(info));
  const unfinalizedParentIds = filteredInfos
    .filter((info) => isUnfinalizedParentStorableSample(info))
    .map((info) => info.idForLookup);
  const unfinalizedParentPromise = bulkFetchParentStorableCapacityModel(unfinalizedParentIds);

  // Build promise for bulk fetching finalized parents
  const finalizedParentIds = filteredInfos
    .filter((info) => !isUnfinalizedParentStorableSample(info))
    .map((info) => info.idForLookup);
  const finalizedParentPromise = doQuerySafe<BulkParentStorableQuery, BulkParentStorableQueryVariables>(
    {query: BULK_PARENT_STORABLE_QUERY},
    {variables: {parentStorableIds: finalizedParentIds}, fetchPolicy: 'network-only'}
  ).then((response) => response.storables);

  // Build promise for bulk fetching wells
  const plateInfos = sampleLookupInfos.filter(
    (info): info is PlatePositionLookupInfo =>
      info.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE && info.plateWellPosition != null
  );
  const plateIds = plateInfos.map((info) => info.idForLookup);
  const wellPromise = doQuerySafe<BulkPlateSizeQuery, BulkPlateSizeQueryVariables>(
    {query: BULK_PLATE_SIZE_QUERY},
    {variables: {plateIds}, fetchPolicy: 'network-only'}
  ).then((response) =>
    Promise.resolve(
      response.plates.map(
        (
          plate,
          index
        ):
          | {isInaccessible: true; value: InaccessibleInfoGraphQL}
          | {isInaccessible: false; value: StorableInput} => {
          if (isInaccessible(plate)) {
            return {isInaccessible: true, value: plate};
          } else {
            const plateSchema = plate.storableSchema;
            assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

            const {plateWellPosition} = plateInfos[index];
            assert(
              plateWellPosition != null,
              'PlateWellPosition must be specified when fetching plate well details for transfer table'
            );

            const {rowIndex, columnIndex} = getPlateWellCoordinates(plateSchema, plateWellPosition);

            return {isInaccessible: false, value: {rowIndex, colIndex: columnIndex, id: plate.id}};
          }
        }
      )
    ).then(
      async (
        inaccessiblesOrInputs: Array<
          | {isInaccessible: true; value: InaccessibleInfoGraphQL}
          | {
              isInaccessible: false;
              value: StorableInput;
            }
        >
      ): Promise<Array<PlateCellInfo | InaccessibleInfoGraphQL>> => {
        const inputs = inaccessiblesOrInputs
          .filter((iOI): iOI is {isInaccessible: false; value: StorableInput} => !iOI.isInaccessible)
          .map((iOI) => iOI.value);
        const {directStorables} = await doQuerySafe<BulkPlateWellInfoQuery, BulkPlateWellInfoQueryVariables>(
          {query: BULK_PLATE_WELL_INFO_QUERY},
          {variables: {inputs}, fetchPolicy: 'network-only'}
        );
        assert(
          directStorables.every((s): s is PlateCellInfo | InaccessibleInfoGraphQL =>
            isPlateCellInfoOrInaccessible(s)
          ),
          'Expected only plates or inaccessible from BULK_PLATE_WELL_INFO_QUERY'
        );
        return [
          ...directStorables,
          ...inaccessiblesOrInputs
            .filter(
              (iOI): iOI is {isInaccessible: true; value: InaccessibleInfoGraphQL} => iOI.isInaccessible
            )
            .map((iOI) => iOI.value),
        ];
      }
    )
  );

  const [containers, entities, uPs, fPs, wells] = await Promise.all([
    containerPromise,
    entityPromise,
    unfinalizedParentPromise,
    finalizedParentPromise,
    wellPromise,
  ]);
  return [...containers, ...entities, ...uPs, ...fPs, ...wells];
}

export const TransferStructuredTableRowUpdater: StructuredTableRowUpdater<
  TransferTableModelInfo | ParentStorableCapacityInfo,
  TransferStructuredTableRowMetadata
> = {
  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache(
    sampleLookupInfo: SampleLookupInfo
  ): TransferTableModelInfo | ParentStorableCapacityInfo | null {
    const {idForLookup} = sampleLookupInfo;
    if (APIUtil.isContainer(idForLookup)) {
      const data = readQuerySafe<BulkContainerInfoQuery, BulkContainerInfoQueryVariables>(
        {query: BULK_CONTAINER_INFO_QUERY},
        {variables: {containerIds: [idForLookup]}, fetchPolicy: 'cache-only'}
      );
      if (data == null || !data.containers?.[0] || isInaccessible(data.containers[0])) {
        return null;
      }
      return data.containers[0];
    } else if (APIUtil.isBiologicalEntity(idForLookup)) {
      const data = readQuerySafe<BulkEntityInfoQuery, BulkEntityInfoQueryVariables>(
        {query: BULK_ENTITY_INFO_QUERY},
        {variables: {entityIds: [idForLookup]}, fetchPolicy: 'cache-only'}
      );
      if (data == null || !data.bioentities?.[0] || isInaccessible(data.bioentities[0])) {
        return null;
      }
      return data.bioentities[0];
    }

    // Check whether the sample for lookup is a matrix plate or well plate. If it's a well plate, then the sample
    // lookup type will be `PLATE_POSITION_DUPLE`, and we don't need to perform `ParentStorableQuery`
    // for it. If it's `DIRECT`, then it must be a matrix plate (and thus, a parent storable of a container).
    const isSampleMatrixPlate =
      APIUtil.isPlate(idForLookup) && sampleLookupInfo.sampleType === PrimarySampleLookupType.DIRECT;

    if (APIUtil.isBox(idForLookup) || APIUtil.isLocation(idForLookup) || isSampleMatrixPlate) {
      if (isUnfinalizedParentStorableSample(sampleLookupInfo)) {
        return readParentStorableCapacityFromCache(idForLookup);
      }
      const data = readQuerySafe<BulkParentStorableQuery, BulkParentStorableQueryVariables>(
        {query: BULK_PARENT_STORABLE_QUERY},
        {variables: {parentStorableIds: [idForLookup]}, fetchPolicy: 'network-only'}
      );
      if (data == null || isInaccessible(data.storables[0])) {
        return null;
      }
      return data.storables[0];
    }

    // If the model is not a container, bioentity, or parent storable, it must be a plate well.
    assert(
      sampleLookupInfo.sampleType === PrimarySampleLookupType.PLATE_POSITION_DUPLE,
      'Since model is plate well, plate position duple should be provided.',
      {
        sampleLookupInfo,
      }
    );
    const {plateWellPosition} = sampleLookupInfo;
    const plateSizeResponse = readQuerySafe<BulkPlateSizeQuery, BulkPlateSizeQueryVariables>(
      {query: BULK_PLATE_SIZE_QUERY},
      {variables: {plateIds: [idForLookup]}, fetchPolicy: 'cache-only'}
    );
    assert(
      plateWellPosition != null,
      'PlateWellPosition must be specified when fetching plate well details for transfer table'
    );
    if (plateSizeResponse == null) {
      return null;
    }

    const plate = plateSizeResponse.plates[0];
    if (isInaccessible(plate)) {
      return null;
    }
    const plateSchema = plate.storableSchema;
    assert(isPlateSchemaInfo(plateSchema), 'Plate size query should have returned a plate schema');

    const {rowIndex, columnIndex} = getPlateWellCoordinates(plateSchema, plateWellPosition);
    const wellData = readQuerySafe<BulkPlateWellInfoQuery, BulkPlateWellInfoQueryVariables>(
      {query: BULK_PLATE_WELL_INFO_QUERY},
      {
        variables: {
          inputs: [
            {
              id: idForLookup,
              rowIndex,
              colIndex: columnIndex,
            },
          ],
        },
        fetchPolicy: 'cache-only',
      }
    );

    if (wellData == null || isInaccessible(wellData.directStorables[0])) {
      return null;
    }

    assert(
      isPlateCellInfoOrInaccessible(wellData.directStorables[0]),
      'Expected only plate or inaccessible from PLATE_WELL_INFO_QUERY'
    );

    return wellData.directStorables[0];
  },

  updateCell: (
    model: TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    if (isInaccessible(model) || isParentStorableCapacityInfo(model)) {
      return 'NO_VALUE';
    }
    const isSampleControlColumn =
      column.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS ||
      column.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS;
    if (!isSampleControlColumn && columnGroup !== column.columnGroup) {
      return 'NO_VALUE';
    }

    const {columnType} = column;
    switch (columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
        return updateBioentityCell(model);
      case TableColumnType.CONTAINERS:
        return updateContainersCell(model);
      case TableColumnType.PLATE:
        return updatePlateCell(model);
      case TableColumnType.BOX:
        return updateBoxCell(model);
      case TableColumnType.PARENT_STORABLE:
        return updateLocationCell(model);
      case TableColumnType.TAG_VALUE:
        return updateTagValueCell(model, column);
      case TableColumnType.PARTIES:
        return updateSampleControlRolesCell(model, columnGroup, column.columnGroup ?? null);
      case TableColumnType.SAMPLE_RESTRICTION_STATUS:
        return updateSampleRestrictionStatusCell(model);
      default:
        return 'NO_VALUE';
    }
  },

  updateRow(
    model: TransferTableModelInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<TransferStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<TransferStructuredTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }
    return {
      ...row,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell),
    };
  },
};
